import { inject, Injectable, signal } from '@angular/core';
import { FeatureFlags } from '@mkp/debug/models';
import { selectTealiumFlags } from '@mkp/debug/state';
import { TealiumConsentDto } from '@mkp/gdpr/models';
import { LanguageCode } from '@mkp/shared/models';
import {
  ClickCategory,
  FullTrackingEvent,
  GlobalCategory,
  TealiumGDPR,
  TrackingEvent,
  UTag,
  WindowWithTealium,
} from '@mkp/tracking/models';
import { getPageCategory } from '@mkp/tracking/util-tracking';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  AsyncSubject,
  lastValueFrom,
  map,
  Observable,
  share,
  take,
  takeWhile,
  tap,
  timer,
} from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class TealiumService {
  readonly isBooted$: Observable<boolean> = timer(0, 1000).pipe(
    map((i) => ({
      i,
      isBooted: isWindowUtag(this.window),
    })),
    takeWhile(({ i, isBooted }) => !isBooted && i < 10, true),
    map(({ isBooted }) => isBooted), // use share operator to prevent multiple timers on multiple subscriptions
    share({
      // use `AsyncSubject` as a connector to emit the value only when the source observable completes
      connector: () => new AsyncSubject(),
    })
  );

  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);
  private readonly tealiumFlags$ = this.store.select(selectTealiumFlags);
  private readonly window = inject(Window) as WindowWithTealium;

  // cheating type here, global-category should be updated before the first event is sent.
  private readonly globalCategory = signal<GlobalCategory>({} as GlobalCategory);

  private readonly viewEventState = signal<{
    referrer: string;
    location: string | null;
  }>({
    referrer: this.window.document.referrer,
    location: null,
  });

  constructor() {
    this.window['utag_cfg_ovrd'] = { noview: true };
  }

  setGlobalCategory = (globalCategory: GlobalCategory): void =>
    this.globalCategory.set(globalCategory);

  setPreferencesFromList(tealiumCategories: string[]): Promise<void | null> {
    return this.waitForIsBootedThen(({ gdpr }) => gdpr.setPreferencesFromList(tealiumCategories));
  }

  private async track(eventType: string, data: TrackingEvent): Promise<FullTrackingEvent> {
    const payload = {
      ...this.globalCategory(),
      ...getPageCategory({
        pageReferrer: this.getReferrer(),
        languageCode: this.interfaceLanguage(),
        windowRef: this.window,
      }),
      ...data,
    };

    if (eventType === 'view') {
      this.viewEventState.set({ location: payload.page_location, referrer: payload.page_referrer });
    }

    return this.waitForIsBootedThen(({ track }) => track(eventType, payload)).then(() => payload);
  }

  view = async (data: TrackingEvent): Promise<FullTrackingEvent> => {
    const event = await this.track('view', data);

    this.debugLogger('trackingView', () =>
      console.log(
        `TealiumService - view: '${event.page_location?.split('/').slice(3).join('/')}'`,
        event
      )
    );

    return event;
  };

  link = async (data: TrackingEvent): Promise<FullTrackingEvent> => {
    const event = await this.track('link', data);

    this.debugLogger('trackingLink', () =>
      console.log(`TealiumService - link: '${event.event_name}'`, event)
    );

    return event;
  };

  click = async (clickCategory: ClickCategory): Promise<FullTrackingEvent> => {
    const clickEvent = await this.track('link', clickCategory);

    this.debugLogger('trackingClick', () =>
      console.log(`TealiumService - click: ${getClickTrackingDebug(clickCategory)}`, clickEvent)
    );

    return clickEvent;
  };

  getConsentState = (): Promise<ReturnType<TealiumGDPR['getConsentState']> | null> =>
    this.waitForIsBootedThen(({ gdpr }) => gdpr.getConsentState());

  setConsentState = (consentState: TealiumConsentDto): Promise<void | null> =>
    this.waitForIsBootedThen(({ gdpr }) => gdpr.setPreferencesValues(consentState));

  private interfaceLanguage = (): LanguageCode | undefined =>
    this.translateService.currentLang?.split('-')[0] as LanguageCode | undefined;

  private getReferrer = (): string => {
    const { location: lastLocation, referrer: lastReferrer } = this.viewEventState();

    // first entry in the app: the initial view event didn't happen yet
    if (lastLocation === null) {
      // when entrying from an external url (like google): return that URL
      // otherwise the user accessed the app directly: return empty string ""
      return this.isExternalEntry(lastReferrer) ? lastReferrer : '';
    }

    return lastLocation === this.window.location.href ? lastReferrer : lastLocation;
  };

  private isExternalEntry(referrer: string): boolean {
    if (referrer === '') {
      // exit early in this case because new URL("") would throw
      return false;
    }

    return new URL(referrer).origin !== this.window.location.origin;
  }

  private waitForIsBootedThen = <T>(callback: (uTag: UTag) => T): Promise<T | null> =>
    lastValueFrom<T>(
      this.isBooted$.pipe(
        filter(Boolean),
        map(() => this.window as WindowWithTealium),
        map((window: WindowWithTealium) => callback(window.utag))
      )
    ).catch(() => null);

  private debugLogger = (
    flag: keyof Pick<FeatureFlags, 'trackingClick' | 'trackingView' | 'trackingLink'>,
    cb: () => void
  ) => {
    this.tealiumFlags$
      .pipe(
        take(1),
        filter((flags) => flags[flag]),
        tap(() => cb())
      )
      .subscribe();
  };
}

const isWindowUtag = (window: Window): window is WindowWithTealium =>
  !!(window as WindowWithTealium).utag;

const getClickTrackingDebug = (clickCategory: ClickCategory): string =>
  `\n - name: ${clickCategory.event_name}\n - type: ${clickCategory.event_type}\n - data-test: ${
    clickCategory.event_component_type
  }\n - innerText: ${clickCategory.event_link_text}\n - url: ${
    (
      clickCategory as {
        event_target_url_full: string;
      }
    )?.event_target_url_full
  }\n - value: ${clickCategory.event_value}`;
